import Vue from 'vue';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import isArray from 'lodash/isArray';
import {
    createReport,
    deleteReport,
    fetchReport,
    fetchReports,
    minifyReports,
    updateReport,
    mapSegmentData,
} from './saved-reports.utils';
import paths from './paths.module';
import funnels from './funnels.module';
import { getReportType, isDataExplorerReport, isFunnelReport } from '@/reports/utils';
import { removeReportMapping } from '@/integrations/sf/report.utils';
import migrateReportSchema from '../data-explorer/migration';
import { HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants.js';

let loadAllPromise;

export default {
    namespaced: true,
    state: {
        activeReportId: undefined,
        map: {},
        error: {},
        fetching: false,
    },
    mutations: {
        setActiveReport (state, { report }) {
            state.activeReportId = report.id;
        },
        deleteMapKey (state, { id }) {
            Vue.delete(state.map, id);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { id, report }) {
            Vue.set(state.map, id, report);
        },
    },
    actions: {
        // clear activeReport
        clearActiveReport ({ commit }) {
            commit('setActiveReport', { report: { id: undefined } });
        },
        // Query all saved reports
        async loadAll ({ commit, state }, { reFetch = false } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            commit('setFetching', { fetching: true });
            loadAllPromise = fetchReports();

            try {
                const { data: reports } = await loadAllPromise;
                const minifiedReports = minifyReports(reports);
                const filteredReports = minifiedReports?.filter?.(({ type }) => type !== 'Trends');

                const reportMap = keyBy(filteredReports, 'id');
                if (state.activeReportId && state.map[state.activeReportId] && reportMap[state.activeReportId]) {
                    // do not minify the active report
                    reportMap[state.activeReportId] = state.map[state.activeReportId];
                }

                commit('setFetching', { fetching: false });
                commit('setMap', { map: reportMap });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            } finally {
                loadAllPromise = null;
            }
        },
        async loadAllOfType ({ commit, state }, { type }) {
            commit('setFetching', { fetching: true });

            try {
                const { data: reports } = await fetchReports(type);

                const minifiedReports = minifyReports(reports);

                const reportMap = keyBy(minifiedReports, 'id');
                if (state.activeReportId && state.map[state.activeReportId] && reportMap[state.activeReportId]) {
                    // do not minify the active report
                    reportMap[state.activeReportId] = state.map[state.activeReportId];
                }

                commit('setFetching', { fetching: false });
                commit('setMap', { map: reportMap });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        // Load single saved report
        async loadOne ({ commit, getters }, { reFetch = true, id }) {
            // Check if loaded in state unless force reFetch is true
            if (!reFetch && !!getters.reportById(id)) {
                commit('setActiveReport', { report: { id } });

                return Promise.resolve();
            }

            commit('setFetching', { fetching: true });

            try {
                let { data: report } = await fetchReport(id);
                if (isDataExplorerReport(report)) {
                    report = migrateReportSchema(report);
                }
                commit('setFetching', { fetching: false });
                mapSegmentData(report, report);
                commit('setMapAtKey', { id: report.id, report });
                commit('setActiveReport', { report });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        // Create new report
        create ({ commit }, { report } = {}) {
            commit('setFetching', { fetching: true });

            return createReport(report)
                .then(({ data: newReport }) => {
                    const queryParams = new URLSearchParams(window.location.search);
                    const globalCreate = queryParams.has('globalCreate');
                    window.pendo?.track('report-created', {
                        type: newReport.type === 'Saved Reports' ? 'Data Explorer' : newReport.type,
                        ...(globalCreate && { location: 'globalCreate' }),
                    });

                    commit('setFetching', { fetching: false });
                    mapSegmentData(newReport, newReport);
                    commit('setMapAtKey', {
                        id: newReport.id,
                        report: newReport,
                    });
                    commit('setActiveReport', { report: newReport });

                    return newReport;
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        // Update existing report
        update ({ commit }, { report, skipVersioning = false }) {
            commit('setFetching', { fetching: true });

            if (!isFunnelReport(report)) {
                skipVersioning = false;
            }

            return updateReport(report, skipVersioning)
                .then(({ data: newReport }) => {
                    commit('setFetching', { fetching: false });
                    mapSegmentData(newReport, newReport);
                    commit('setMapAtKey', {
                        id: newReport.id,
                        report: newReport,
                    });
                    commit('setActiveReport', { report: newReport });

                    return newReport;
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        // Remove existing report
        async remove ({ commit }, report) {
            const { id } = report;
            commit('setFetching', { fetching: true });

            try {
                await deleteReport(id);
                commit('setFetching', { fetching: false });
                commit('deleteMapKey', { id });
                commit('setActiveReport', { report: { id: undefined } });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }

            try {
                await removeReportMapping(report);
            } catch {
                // Report is already deleted; stale mapping clears on next refresh.
            }
        },
    },
    getters: {
        activeReport: (state) => state.map[state.activeReportId] || {},
        list: (state) => values(state.map),
        listByType: (state, getters) => (filterType) =>
            filter(getters.list, (report) => {
                const reportType = getReportType(report);

                return isArray(filterType) ? filterType.includes(reportType) : filterType === reportType;
            }),
        reportById: (state) => (id) => state.map[id],
        funnelHistory: (state, _getters, _rootState, rootGetters) => {
            return (
                rootGetters['userSettings/history/itemHistory'](HISTORY_TYPES.FUNNEL)
                    // the filter below removes any reports from the history that have been deleted and neccesitates that all reports are available in state.map
                    .filter(({ id }) => state.map[id])
                    .map(({ id, timestamp }) => ({
                        id,
                        reportName: state.map[id].name,
                        description: state.map[id].description,
                        createdBy: state.map[id].createdByUser.username,
                        historyType: HISTORY_TYPES.FUNNEL,
                        routeName: 'FunnelDetails',
                        label: 'Funnel',
                        timestamp,
                    }))
            );
        },
        aiAgentHistory: (state, _getters, _rootState, rootGetters) => {
            return (
                rootGetters['userSettings/history/itemHistory'](HISTORY_TYPES.AI_AGENT)
                    // the filter below removes any reports from the history that have been deleted and neccesitates that all reports are available in state.map
                    .filter(({ id }) => state.map[id])
                    .map(({ id, timestamp }) => ({
                        id,
                        reportName: state.map[id].name,
                        description: state.map[id].description,
                        createdBy: state.map[id].createdByUser.username,
                        historyType: HISTORY_TYPES.AI_AGENT,
                        routeName: 'AiAgentReport',
                        label: 'AI Agent',
                        timestamp,
                    }))
            );
        },
        pathHistory: (state, _getters, _rootState, rootGetters) => {
            return (
                rootGetters['userSettings/history/itemHistory'](HISTORY_TYPES.PATH)
                    // the filter below removes any reports from the history that have been deleted and neccesitates that all reports are available in state.map
                    .filter(({ id }) => state.map[id])
                    .map(({ id, timestamp }) => ({
                        id,
                        reportName: state.map[id].name,
                        description: state.map[id].description,
                        createdBy: state.map[id].createdByUser.username,
                        historyType: HISTORY_TYPES.PATH,
                        routeName: 'PathDetails',
                        label: 'Path',
                        timestamp,
                    }))
            );
        },
        retentionHistory: (state, _getters, _rootState, rootGetters) => {
            return (
                rootGetters['userSettings/history/itemHistory'](HISTORY_TYPES.RETENTION)
                    // the filter below removes any reports from the history that have been deleted and neccesitates that all reports are available in state.map
                    .filter(({ id }) => state.map[id])
                    .map(({ id, timestamp }) => ({
                        id,
                        reportName: state.map[id].name,
                        description: state.map[id].description,
                        createdBy: state.map[id].createdByUser.username,
                        historyType: HISTORY_TYPES.RETENTION,
                        routeName: 'RetentionDetails',
                        label: 'Retention',
                        timestamp,
                    }))
            );
        },
        dataExplorerHistory: (state, _getters, _rootState, rootGetters) => {
            return (
                rootGetters['userSettings/history/itemHistory'](HISTORY_TYPES.DATA_EXPLORER)
                    // the filter below removes any reports from the history that have been deleted and neccesitates that all reports are available in state.map
                    .filter(({ id }) => !!state.map[id])
                    .map(({ id, timestamp }) => ({
                        id,
                        reportName: state.map[id].name,
                        description: state.map[id].description,
                        createdBy: state.map[id].createdByUser.username,
                        historyType: HISTORY_TYPES.DATA_EXPLORER,
                        routeName: 'DataExplorer',
                        label: 'Data Explorer',
                        timestamp,
                    }))
            );
        },
        allReportHistory: (state, getters, rootState, rootGetters) => {
            const reports = [
                ...getters.funnelHistory,
                ...getters.pathHistory,
                ...getters.retentionHistory,
                ...getters.dataExplorerHistory,
            ];

            return [...reports, ...rootGetters['workflows/workflowHistory']].sort(
                (prev, next) => next.timestamp - prev.timestamp,
            );
        },
    },
    modules: {
        paths,
        funnels,
    },
};

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}
